import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { TiptapNodeType } from '../tiptapTypes';
import { shouldOmitBlockquoteQuotes } from './shouldOmitBlockquoteQuotes';

const BLOCKQUOTE_SELF_QUOTED_PLUGIN_NAME = 'blockquoteSelfQuotedDecoration';

/**
 * Class applied to blockquotes whose text is already wrapped in quotes, so the stylesheet
 * can suppress the decorative CSS quotes for them.
 */
export const BLOCKQUOTE_OMIT_QUOTES_CLASS = 'mn-blockquote-omit-quotes';

/**
 * Builds node decorations that flag each self-quoted blockquote in the document.
 *
 * This is a purely visual concern: decorations never alter the underlying document, they
 * are recomputed from the current document on every render.
 */
export const getBlockquoteSelfQuotedDecorations = (doc: ProseMirrorNode): Decoration[] => {
    const decorations: Decoration[] = [];

    doc.descendants((node, position) => {
        if (node.type.name !== TiptapNodeType.blockquote) return;

        if (shouldOmitBlockquoteQuotes(node)) {
            decorations.push(
                Decoration.node(
                    position,
                    position + node.nodeSize,
                    // attrs: applies the class to the rendered node
                    { class: BLOCKQUOTE_OMIT_QUOTES_CLASS },
                    // spec: mirrors the class so it can be asserted in tests (decoration
                    // attrs aren't publicly readable)
                    { class: BLOCKQUOTE_OMIT_QUOTES_CLASS },
                ),
            );
        }

        // Blockquotes can't be nested, so there's no need to descend further.
        return false;
    });

    return decorations;
};

/**
 * Adds a class to blockquotes whose text already includes surrounding quotes, allowing the
 * stylesheet to avoid rendering a redundant second set of decorative quotes.
 *
 * The decorations are held in plugin state and only recomputed when the document changes,
 * so selection-only transactions (e.g. moving the cursor) don't trigger a full doc walk.
 */
export const createBlockquoteSelfQuotedDecorationPlugin = (): Plugin<DecorationSet> => {
    const pluginKey = new PluginKey<DecorationSet>(BLOCKQUOTE_SELF_QUOTED_PLUGIN_NAME);

    return new Plugin<DecorationSet>({
        key: pluginKey,
        state: {
            init: (_config, state) =>
                DecorationSet.create(state.doc, getBlockquoteSelfQuotedDecorations(state.doc)),
            apply: (transaction, currentDecorations, _oldState, newState) => {
                // Omission depends on text content, so a full recompute is only needed when
                // the document actually changes. Otherwise the existing set still applies.
                if (!transaction.docChanged) return currentDecorations;

                return DecorationSet.create(newState.doc, getBlockquoteSelfQuotedDecorations(newState.doc));
            },
        },
        props: {
            decorations(state) {
                return pluginKey.getState(state);
            },
        },
    });
};
